import { useEffect, useMemo, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";

import { listFiltersService, ensure, matchQuery, getFileExtension } from "../../../utils";
import { ListTableSort } from "../../lists/ListTableSort";
import { BucketObjectType } from "../types/BucketObjectType";
import { LegacyBucketObjectItem, LegacyBucketFileItem, LegacyBucketFolderItem } from "./LegacyBucketObjectItem";

export interface LegacyBucketExplorerFilters {
    path: string | undefined;
    filename: string | undefined;
}

const defaultFilters: LegacyBucketExplorerFilters = {
    path: undefined,
    filename: undefined
};

export const useLegacyBucketExplorerFilters = () => {
    const location = useLocation();
    const history = useHistory();

    const filtersService = useMemo(() => listFiltersService<LegacyBucketExplorerFilters>(defaultFilters), []);

    const [pathFolders, setPathFolders] = useState<string[]>(() => {
        const { path } = filtersService.getSavedFilters(location.search);

        return path?.split("\\") ?? [];
    });
    const [sort, setSort] = useState<ListTableSort>({ by: "type", order: "asc" });
    const [fileName, setFileName] = useState<string>();

    useEffect(() => {
        filtersService.saveFilters(location.pathname, history, {
            path: pathFolders.length ? pathFolders.join("\\") : undefined,
            filename: fileName
        });
    }, [location.pathname, history, pathFolders, fileName]);

    const openFolder = (folderName: string) => setPathFolders((items) => [...items, folderName]);

    const goBackToFolder = (folderName: string) =>
        setPathFolders((items) => items.slice(0, items.findIndex((folder) => folder === folderName) + 1));

    const root = (): void => {
        setPathFolders([]);
    };

    const search = (filename: string) => {
        setPathFolders([]);

        if (!filename) {
            setFileName(undefined);
        } else {
            setFileName(filename);
        }
    };

    const sortBucketContent = (
        content: LegacyBucketObjectItem[],
        sortConfig: ListTableSort
    ): LegacyBucketObjectItem[] => {
        const contentCopy = [...content];

        switch (sortConfig.by) {
            case "name": {
                sortByName(contentCopy, sortConfig.order === "desc");
                break;
            }

            case "sizeInBytes": {
                sortByFileSize(contentCopy, sortConfig.order === "desc");
                break;
            }

            case "type": {
                sortByType(contentCopy, sortConfig.order === "desc");
                break;
            }

            case "lastModified": {
                sortByLastModified(contentCopy, sortConfig.order === "desc");
                break;
            }

            default: {
                throw new Error(`Sort by '${sortConfig.by}' is not supported`);
            }
        }

        return contentCopy;
    };

    const filterBucketContentByFolders = (
        bucketContent: LegacyBucketObjectItem[],
        pathFolderNames: string[]
    ): LegacyBucketObjectItem[] => {
        if (!bucketContent.length || !pathFolderNames.length) {
            return bucketContent;
        }

        const [firstName, ...theRest] = pathFolderNames;

        const folder = bucketContent.filter(isFolderItem).find((item) => item.name === firstName);
        ensure(folder);

        return filterBucketContentByFolders(folder.content, theRest);
    };

    const filterBucketContentByFileName = (
        content: LegacyBucketObjectItem[],
        filename: string,
        parentFolderName?: string
    ): LegacyBucketFileItem[] => {
        return content.reduce<LegacyBucketFileItem[]>((newContent, item) => {
            if (item.type === BucketObjectType.Folder) {
                newContent.push(...filterBucketContentByFileName(item.content, filename, item.name));
            }

            if (item.type === BucketObjectType.File && matchQuery(item.name, filename)) {
                const itemName = parentFolderName === undefined ? item.name : `${parentFolderName} > ${item.name}`;

                newContent.push({ ...item, name: itemName });
            }

            return newContent;
        }, []);
    };

    return {
        fileName,
        pathFolders,
        sort,
        search,
        openFolder,
        goBackToFolder,
        root,
        setSort,
        setFileName,
        sortBucketContent,
        filterBucketContentByFolders,
        filterBucketContentByFileName
    };
};

const sortByName = (content: LegacyBucketObjectItem[], reverse?: boolean): void => {
    content.sort((a, b) => (a.name.toLowerCase() > b.name.toLowerCase() ? 1 : -1));

    if (reverse) {
        content.reverse();
    }
};

const sortByFileSize = (content: LegacyBucketObjectItem[], reverse?: boolean): void => {
    content.sort((a, b) => {
        return a.sizeInBytes > b.sizeInBytes ? 1 : -1;
    });

    content.sort((a, b) => {
        if (a.sizeInBytes === b.sizeInBytes) {
            return a.name.toLowerCase() > b.name.toLowerCase() ? 1 : -1;
        }

        return 0;
    });

    if (reverse) {
        content.reverse();
    }
};

const sortByLastModified = (content: LegacyBucketObjectItem[], reverse?: boolean): void => {
    content.sort((a, b) => {
        if (a.type === BucketObjectType.Folder || b.type === BucketObjectType.Folder) {
            return b.type === BucketObjectType.Folder ? 1 : -1;
        }

        return new Date(a.lastModified) > new Date(b.lastModified) ? 1 : -1;
    });

    content.sort((a, b) => {
        if (a.lastModified === b.lastModified) {
            return a.name.toLowerCase() > b.name.toLowerCase() ? 1 : -1;
        }

        return 0;
    });

    if (reverse) {
        content.reverse();
    }
};

const sortByType = (content: LegacyBucketObjectItem[], reverse: boolean): void => {
    content.sort((a, b) => {
        if (a.type === BucketObjectType.Folder || b.type === BucketObjectType.Folder) {
            return b.type === BucketObjectType.Folder ? 1 : -1;
        }

        return getFileExtension(a.name) > getFileExtension(b.name) ? 1 : -1;
    });

    content.sort((a, b) => {
        if (a.type === b.type && getFileExtension(a.name) === getFileExtension(b.name)) {
            return a.name.toLowerCase() > b.name.toLowerCase() ? 1 : -1;
        }

        return 0;
    });

    if (reverse) {
        content.reverse();
    }
};

const isFolderItem = (item: LegacyBucketObjectItem): item is LegacyBucketFolderItem =>
    item.type === BucketObjectType.Folder;
